import React, { createRef, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Badge, Divider, Grid, Typography, Switch } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import { getValue } from "components/Screens/CommonUtils";
import EmploymentInformation from "components/Screens/Customers/CustomerDetials/CustomerProfile/EmploymentInformation";
import PersonalInfo from "components/Screens/Customers/CustomerDetials/CustomerProfile/PersonalInfo";
import LastActive from "components/common/LastActive";
import BankInfo from "components/Screens/Customers/CustomerDetials/CustomerProfile/BankInfo";
import ResendOnboardingEmail from "components/Screens/Customers/CustomerDetials/CustomerProfile/ResendOnboardingEmail";
import AvatarIcon from "components/Icons/AvatarIcon";
import { useFlags } from "launchdarkly-react-client-sdk";

import { GetCustomerDetails } from "store/actions/Customers/GetCustomerDetails";

import { baseUrl } from "util/APIBaseUrl";
import { updateCustomerSetting } from "services/Admin/Dashboard";

import "./Profile.scss";

function NewProfile() {
  const photoInput = createRef();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const { clockinCustomerExperience, skipKickoffCall } = useFlags();

  const customerDetails = useSelector((state) => state.GetCustomerDetails.customerDetails);
  const { lastActive, lastActiveEmail, showResendEmail, customerId } = customerDetails?.payload || {};

  const [talentNetworkMethod, setTalentNetworkMethod] = useState(
    customerDetails?.payload?.talentNetworkVisibility || false
  );
  const [clockinExperienceMethod, setClockinExperienceMethod] = useState(
    customerDetails?.payload?.clockinExperienceEnabled || false
  );
  const [skipKickoffCallMethod, setSkipKickoffCallMethod] = useState(
    customerDetails?.payload?.skipKickoffCall || false
  );
  const [enableEnhancedPortalExperienceMethod, setEnableEnhancedPortalExperienceMethod] = useState(
    customerDetails?.payload?.enableEnhancedPortalExperience || false
  );

  useEffect(() => {
    setTalentNetworkMethod(customerDetails?.payload?.talentNetworkVisibility || false);
  }, [customerDetails?.payload?.talentNetworkVisibility]);

  useEffect(() => {
    setClockinExperienceMethod(customerDetails?.payload?.clockinExperienceEnabled || false);
  }, [customerDetails?.payload?.clockinExperienceEnabled]);

  useEffect(() => {
    setSkipKickoffCallMethod(customerDetails?.payload?.skipKickoffCall || false);
  }, [customerDetails?.payload?.skipKickoffCall]);

  useEffect(() => {
    setEnableEnhancedPortalExperienceMethod(customerDetails?.payload?.enableEnhancedPortalExperience || false);
  }, [customerDetails?.payload?.enableEnhancedPortalExperience]);

  const postLogo = async (actions) => {
    const response = await fetch(`${baseUrl}/${actions.request}`, {
      method: "POST",
      headers: {},
      body: actions.formData,
    });
    if (!response.ok) {
      return;
    }
    window.location.reload();
    await response.json();
  };

  const uploadProfilePic = () => {
    photoInput.current.click();
  };

  const handleImageChange = (e) => {
    let logoRequest = "";
    const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
    e.preventDefault();
    const inFile = e.target.files[0];
    const formData = new FormData();
    formData.append("file", inFile);
    if (loggedInUser?.type === "edge") {
      logoRequest = `customer/${customerDetails?.payload?.customerId}/logo`;
    }

    postLogo({
      request: logoRequest,
      formData,
    });
  };

  const handleCustomerToggle = async (field, value, setOptimistic) => {
    try {
      setOptimistic(value);
      await updateCustomerSetting(customerId, { [field]: value });
      dispatch(GetCustomerDetails({ customerId }));
    } catch (error) {
      setOptimistic(!value);
    }
  };

  return (
    <Grid
      item
      container
      direction="row"
      xl={12}
      lg={12}
      md={12}
      sm={12}
      xs={12}
      sx={{ px: 10, py: 5 }}
      columnSpacing={7}
    >
      <Grid item container direction="column" xl={5} lg={5} md={6} sm={12} xs={12} rowSpacing={10}>
        <Grid item container direction="row">
          <PersonalInfo />
        </Grid>
        <Grid item container direction="row">
          <EmploymentInformation />
        </Grid>
        <Grid item container direction="row">
          <BankInfo />
          <div className="flex flex-col gap-1">
            <div className="talent-network-wrapper">
              <p className="payment-method-label">Enable Talent Network</p>
              <div className="payment-method-right">
                <Switch
                  size="small"
                  checked={talentNetworkMethod}
                  onChange={(event) =>
                    handleCustomerToggle("talentNetworkVisibility", event.target.checked, setTalentNetworkMethod)
                  }
                />
              </div>
            </div>
            <div className="talent-network-wrapper">
              <p className="payment-method-label">Enable Enhanced Portal Experience</p>
              <div className="payment-method-right">
                <Switch
                  size="small"
                  checked={enableEnhancedPortalExperienceMethod}
                  onChange={(event) =>
                    handleCustomerToggle(
                      "enableEnhancedPortalExperience",
                      event.target.checked,
                      setEnableEnhancedPortalExperienceMethod
                    )
                  }
                />
              </div>
            </div>
            {clockinCustomerExperience && (
              <div className="talent-network-wrapper">
                <p className="payment-method-label">Enable Clock-in Experience</p>
                <div className="payment-method-right">
                  <Switch
                    size="small"
                    checked={clockinExperienceMethod}
                    onChange={(event) =>
                      handleCustomerToggle("clockinExperienceEnabled", event.target.checked, setClockinExperienceMethod)
                    }
                  />
                </div>
              </div>
            )}
            {skipKickoffCall && (
              <div className="talent-network-wrapper">
                <p className="payment-method-label">Skip Kickoff Call</p>
                <div className="payment-method-right">
                  <Switch
                    size="small"
                    checked={skipKickoffCallMethod}
                    onChange={(event) =>
                      handleCustomerToggle("skipKickoffCall", event.target.checked, setSkipKickoffCallMethod)
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </Grid>
      </Grid>
      {!isMobile ? (
        <Grid item xl={2} lg={2} alignItems="center">
          <Divider orientation="vertical" variant="middle" flexItem sx={{ color: "#9C9EB5", height: "270px" }} />
        </Grid>
      ) : null}
      <Grid item container direction="column" alignItems="center" rowSpacing={2} xl={5} lg={5} md={4} sm={12} xs={12}>
        <Grid item>
          <Badge
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            variant="standard"
            badgeContent={<EditOutlinedIcon fontSize="large" />}
            overlap="circular"
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: "#F7F7F7",
                minWidth: "45px",
                height: "45px",
                borderRadius: "25px",
                boxShadow: "0px 4px 10px 0px #080D461F",
              },
            }}
            onClick={() => {
              uploadProfilePic();
            }}
          >
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              ref={photoInput}
              style={{ display: "none" }}
            />
            <AvatarIcon
              className="customer-profile-avatar-sidebar-nested"
              shape="rounded"
              size={{
                width: "25vh",
                height: "25vh",
                borderRadius: "50%",
              }}
              source={`${baseUrl}/customer/${customerDetails?.payload?.customerId}/logo?id=${customerDetails?.payload?.pictureVersion}`}
              alt={customerDetails?.payload?.companyName}
            />
          </Badge>
        </Grid>
        <Grid item>
          <Typography variant="h2" sx={{ letterSpacing: "3px" }}>
            {getValue(customerDetails?.payload?.companyName)}
          </Typography>
        </Grid>
        <LastActive {...{ lastActive, lastActiveEmail }} />
        {showResendEmail && <ResendOnboardingEmail customerId={customerId} />}
      </Grid>
    </Grid>
  );
}

export default NewProfile;
